import { useMemo, useRef, useCallback } from 'react'
import { toast } from 'sonner'
import type { Deal, DealStage, MainTab } from '@/types'
import { useDealsDnd } from '@/hooks/useDealsDnd'
import { useAuthStore } from '@/stores/authStore'
import { JourneyColumn } from '@/components/Deals/JourneyColumn'
import { JourneyPipelineBar } from '@/components/Deals/JourneyPipelineBar'
import '@/components/DealsV2/deals-v2-kanban.css'
import type { JourneyMacroId } from '@/components/Deals/dealsJourney.constants'
import { getJourneyMacrosForTab } from '@/components/Deals/dealsJourney.constants'
import { groupDealsByJourneyMacro } from '@/components/Deals/dealsJourney.util'

interface DealsJourneyViewProps {
  visibleDeals: Deal[]
  mainTab: MainTab
  onSelectDeal: (deal: Deal) => void
  onMoveStage?: (dealId: string, newStage: DealStage) => void
  areValuesVisible?: boolean
  error?: string | null
  onRetry?: () => void
}

export function DealsJourneyView({
  visibleDeals,
  mainTab,
  onSelectDeal,
  onMoveStage,
  areValuesVisible = false,
  error,
  onRetry,
}: DealsJourneyViewProps) {
  const macros = getJourneyMacrosForTab(mainTab)
  const userRole = useAuthStore((s) => s.user?.role)
  const grouped = useMemo(
    () => groupDealsByJourneyMacro(visibleDeals, mainTab),
    [visibleDeals, mainTab],
  )
  const columnRefs = useRef<Partial<Record<JourneyMacroId, HTMLDivElement | null>>>({})

  const handleBlocked = useCallback(
    (result: { allowed: boolean; reason?: string }, targetStage: DealStage) => {
      if (result.reason) toast.error(result.reason, { id: `dnd-journey-${targetStage}` })
    },
    [],
  )

  const dnd = useDealsDnd(
    visibleDeals,
    (dealId, newStage) => {
      const current = visibleDeals.find((d) => d.id === dealId)
      if (current?.etapa === newStage) return
      onMoveStage?.(dealId, newStage)
    },
    handleBlocked,
    userRole,
  )

  const draggedDeal = dnd.draggedDealId
    ? (visibleDeals.find((d) => d.id === dnd.draggedDealId) ?? null)
    : null

  const scrollToMacro = useCallback((macroId: JourneyMacroId) => {
    columnRefs.current[macroId]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'start',
    })
  }, [])

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center gap-3 rounded-2xl border border-danger/20 bg-danger/5 px-6 py-16 text-center'>
        <p className='text-sm font-medium text-text-primary'>{error}</p>
        {onRetry ? (
          <button
            type='button'
            onClick={onRetry}
            className='rounded-lg border border-accent/30 bg-accent/10 px-4 py-2 text-sm font-medium text-accent'
          >
            Tentar novamente
          </button>
        ) : null}
      </div>
    )
  }

  const total = visibleDeals.length

  return (
    <div className='deals-journey-view flex flex-col gap-3'>
      <JourneyPipelineBar
        macros={macros}
        grouped={grouped}
        total={total}
        areValuesVisible={areValuesVisible}
        onMacroClick={scrollToMacro}
      />

      {total === 0 ? (
        <div className='rounded-2xl border border-dashed border-border-subtle px-6 py-16 text-center'>
          <p className='text-sm font-medium text-text-muted'>Nenhuma negociação nesta visão</p>
        </div>
      ) : (
        <div className='deals-journey-board'>
          <div className='sr-only' aria-live='polite'>
            {dnd.isDragging && draggedDeal ? `Arrastando ${draggedDeal.empresaNome}` : ''}
          </div>
          {macros.map((macro) => (
            <JourneyColumn
              key={macro.id}
              ref={(node) => {
                columnRefs.current[macro.id] = node
              }}
              macro={macro}
              deals={grouped[macro.id] ?? []}
              onSelectDeal={onSelectDeal}
              onMoveStage={onMoveStage}
              areValuesVisible={areValuesVisible}
              dnd={onMoveStage ? dnd : undefined}
              draggedDeal={draggedDeal}
              userRole={userRole}
            />
          ))}
        </div>
      )}
    </div>
  )
}
